/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import React from 'react';

import { Form } from '@mairistem/composants';

import { ChampProfession, STYLE_TITRE_SECTION } from '@/components/Common';
import {
  Section,
  SectionHeader,
  ParentForm,
  TypeComponent,
} from '@mairistem/administres-utils';

interface InformationsParentProps {
  /** Numéro du parent (1 ou 2) : rang, et clé des champs `parent{n}.*` */
  numeroParent: number;
  /**
   * Rôle affiché dans les titres du `ParentForm` (« Père », « Mère »), à la
   * place de « Parent N ». Sur une reconnaissance, le rôle est porté par la
   * colonne en base (`ar_per_*` / `ar_mer_*`) et non par un rang.
   */
  libelleParent?: string;
  /**
   * Titre de la `Section`. Fourni séparément de `libelleParent` parce que
   * l'article varie (« SUR LE PÈRE » / « SUR LA MÈRE ») : le déduire dans le
   * composant reviendrait à y coder de la grammaire.
   */
  titreSection?: string;
  /**
   * Verrouille le champ « Sexe ». En reconnaissance la civilité découle du rôle
   * et est préremplie (cf. `constants/rolesParentsParTypeActe`).
   */
  disabledSexe?: boolean;
  /**
   * Ce parent peut-il être déclaré de filiation inconnue (défaut `true`) ? Faux
   * pour le parent AUTEUR d'une reconnaissance, qui reconnaît l'enfant — et donc
   * pour les deux sur « le père et la mère ».
   */
  filiationInconnuePossible?: boolean;
  /** Individu du parent (état civil, lieu de naissance, profession…) */
  parent: Record<string, any>;
  /** Filiation inconnue pour ce parent */
  filiationInconnue?: boolean;
  errors?: Record<string, string>;
  onChange: (event: React.SyntheticEvent | null, payload: any) => void;
  /**
   * Variante d'affichage de l'état civil (défaut `NAISSANCE`, comportement
   * historique inchangé). En reconnaissance, `RECONNAISSANCE` fait dire
   * « Parent inconnu » plutôt que « Parent décédé »
   * (cf. `constants/typeComponentByTypeActe`).
   */
  typeComponent?: TypeComponent;
  /**
   * Affiche le champ « Profession » sous le lieu de naissance. Réservé à
   * l'acte de naissance : `ec_actr` n'a pas de colonne de profession, et la
   * fiche de reconnaissance ne l'affiche pas. Le `ParentForm` de la lib ne
   * fournit pas ce champ, il est donc rendu ici.
   */
  showProfession?: boolean;
}

const InformationsParent = (props: InformationsParentProps) => {
  const {
    numeroParent,
    libelleParent,
    titreSection,
    disabledSexe = false,
    filiationInconnuePossible = true,
    parent,
    filiationInconnue = false,
    errors = {},
    onChange,
    typeComponent = TypeComponent.NAISSANCE,
    showProfession = false,
  } = props;

  return (
    <Section
      title={titreSection ?? `INFORMATIONS SUR LE PARENT ${numeroParent}`}
      height="auto"
    >
      {/* @ts-ignore */}
      <Form style={{ padding: '8px 14px' }}>
        <ParentForm
          numeroParent={numeroParent}
          libelleParent={libelleParent}
          disabledSexe={disabledSexe}
          filiationInconnuePossible={filiationInconnuePossible}
          filiationInconnue={filiationInconnue}
          typeComponent={typeComponent}
          parent={parent}
          errors={errors}
          onChange={onChange}
        />
        {showProfession && (
          <>
            {/*
              Titre de section OBLIGATOIRE : sans lui, la profession se rendait
              juste sous le bloc « Lieu de naissance » du `ParentForm`, et se
              lisait comme un champ de ce lieu. « Divers » est le libellé de la
              maquette, et celui de la fiche (section `DIVERS`).
            */}
            {/* @ts-ignore */}
            <SectionHeader
              title="Divers"
              required={false}
              style={STYLE_TITRE_SECTION}
            />
            <div style={{ padding: '0 1em' }}>
              <ChampProfession
                name={`parent${numeroParent}.profession`}
                value={parent?.profession}
                errors={errors}
                cleErreur="profession"
                disabled={filiationInconnue}
                onChange={onChange}
              />
            </div>
          </>
        )}
      </Form>
    </Section>
  );
};

export default InformationsParent;
